import React, { useEffect } from "react";
import logo from "../assets/SVG/Logo_new.svg";
import { getData, removeData, saveData } from "@/utils/localStorage";
import { authStorageUser } from "@/config/constants/StorageKeys";
import { ComponentType } from "@/store/slices/componentSlice";
import { ListContentType } from "@/types/ContentTypes";
import {
  checkPermissionByKey,
  checkPermissionByKeyAndValue,
  isBitramp,
  mapComponentToPermKey,
} from "@/utils/utils";
import { AuthResponse } from "@/types/Auth.tsx";
import { useQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/types/Api.tsx";
import { User } from "@/types/User.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import {
  CoPQueryKey,
  GenericStatusQueryKey,
  InboxUnreadQueryKey,
} from "@/config/constants/QueryKeys.tsx";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
import Loader from "./Loader";
import { GeneralStatus } from "@/types/BusinessInfoNEW";
import logo_Bitramp from "../assets/SVG/logo_Bitramp.svg";

interface SidebarProps {
  onSelect: (component: ComponentType) => void;
  activeComponent: ComponentType;
  permissionsList: string[];
  allPermissions: ListContentType[] | null;
}

const Sidebar: React.FC<SidebarProps> = ({
  onSelect,
  activeComponent,
  permissionsList,
}) => {
  const [user, setUser] = React.useState(
    () => getData<AuthResponse>(authStorageUser)?.user
  );
  //TODO UPDATE THE PERMISSIONS LIST

  const { data: notifications, isLoading } = useQuery<ApiResponse<number>>({
    queryFn: async () =>
      GenericApi.get<number>(`${ApiRoutes.INBOX}/unread-count`),
    queryKey: [InboxUnreadQueryKey],
  });

  useQuery<ApiResponse<User>>({
    queryFn: async () =>
      GenericApi.get<User>(
        `${ApiRoutes.USERS}/${user?.id}`,
        undefined,
        (data) => {
          const loginData = getData<AuthResponse>(authStorageUser);

          saveData(authStorageUser, {
            ...loginData,
            user: data,
          });

          setUser(data);
        }
      ),
    queryKey: [CoPQueryKey],
  });

  const { data: userGeneralStatus } = useQuery<ApiResponse<GeneralStatus>>({
    queryFn: async () =>
      GenericApi.get<GeneralStatus>(`${ApiRoutes.GENERALSTATUS}`),
    queryKey: [GenericStatusQueryKey],
  });

  const canAccessTransactionClient =
    user?.is_staff === false &&
    userGeneralStatus?.data?.status?.status === "approved";

  const canAccessExchange = checkPermissionByKey(
    permissionsList,
    "exchangerate"
  );

  const canAccessTransactionsNEW =
    checkPermissionByKeyAndValue(permissionsList, "view", "transaction") ||
    user?.is_superuser;

  const canAccessClientsNEW =
    checkPermissionByKeyAndValue(permissionsList, "view", "clientinfo") ||
    user?.is_superuser;

  const canAccessInbox = true;
  const canAccessDashboard = true;
  const canAccessStatus = false;
  const canAccessReports = false;

  const isSuperuser = user?.is_staff;

  const canSelect = (component: ComponentType) => {
    const canNav = mapComponentToPermKey(component);
    const isAllowed =
      (canNav &&
        permissionsList.find((perm) => perm.split("_")[1] === canNav) !==
          undefined) ||
      isSuperuser;

    if (
      isAllowed ||
      component === "Clude" ||
      component === "Settings" ||
      component === "Dashboard"
    ) {
      onSelect(component);
    }
  };

  useEffect(() => {
    const root = document.documentElement;

    const componentOrder = !isSuperuser
      ? [
          "Inbox",
          "Dashboard",
          "Exchange",
          "Transactions",
          "POS Clients",
          "Clude",
          "Reports",
          "Settings",
        ]
      : [
          "Inbox",
          "Dashboard",
          "Exchange",
          "Transactions",
          "Clude",
          "Reports",
          "Logs",
          "Clients",
          "Settings",
          "Status",
        ];

    const activePosition = componentOrder.indexOf(activeComponent);

    root.style.setProperty(
      "--active-position",
      activePosition !== -1 ? activePosition.toString() : "9"
    );
  }, [activeComponent, isSuperuser]);

  const handleLogout = () => {
    removeData(authStorageUser);
    localStorage.removeItem("client");
    window.location.href = "/login";
  };

  const { selectedClient } = useSelector((state: RootState) => state.client);

  if (isLoading) {
    <Loader />;
  }

  return (
    <div className="sidebar">
      <div className="sidebar__container">
        <div
          onClick={
            isSuperuser && selectedClient
              ? () => {}
              : () => canSelect("Dashboard")
          }
          className="sidebar__container__logo"
        >
          {isBitramp() ? (
            <img className="w-[9rem] h-[4rem]" src={logo_Bitramp} alt="logo" />
          ) : (
            <img src={logo} alt="logo" />
          )}
        </div>
        <div className="sidebar__container__options">
          <div
            onClick={
              isSuperuser && selectedClient ? () => {} : () => onSelect("Inbox")
            }
            className={`${
              canAccessInbox || isSuperuser ? "" : "inaccesable"
            } sidebar__container__options__button Text dark-blue bold ${
              activeComponent === "Inbox" ? "active" : ""
            } ${isSuperuser && selectedClient ? "inaccesable" : ""} `}
          >
            <svg
              width="20"
              height="19"
              viewBox="0 0 19 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="z-[9999999]"
            >
              <g id="Alert-Bell-Notification-2--Streamline-Ultimate.svg">
                <path
                  id="Vector"
                  d="M11.0331 19.7245H6.81625C6.70049 19.7284 6.60475 19.8161 6.59085 19.9311C6.58625 20.0187 6.58625 20.1065 6.59085 20.1941C6.59085 22.0015 8.54748 23.1312 10.1127 22.2275C10.8392 21.808 11.2867 21.0329 11.2867 20.1941C11.2914 20.1065 11.2914 20.0187 11.2867 19.9311C11.2719 19.805 11.1596 19.7135 11.0331 19.7245Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  id="Vector_2"
                  d="M11.7377 4.69797C11.7351 4.13964 11.847 3.58668 12.0664 3.07321C11.5214 2.78724 10.9353 2.58762 10.329 2.48153V1.41088C10.329 0.326435 9.15501 -0.351395 8.21584 0.19083C7.77999 0.442489 7.51147 0.907568 7.51147 1.41088V2.49093C6.53704 2.67512 5.61991 3.08701 4.83484 3.69306C2.80318 5.36458 1.64484 7.87149 1.68864 10.502V11.2158C1.68787 13.6106 1.12494 15.9717 0.0450945 18.1093C-0.0640597 18.343 0.0314864 18.6211 0.261103 18.7385C0.325982 18.7685 0.396324 18.7845 0.467719 18.7855H17.3727C17.632 18.7855 17.8423 18.5752 17.8423 18.3159C17.8423 18.2444 17.8263 18.1738 17.7953 18.1093C16.7156 15.9717 16.1526 13.6106 16.1518 11.2158V10.502C16.1552 9.97014 16.1111 9.43894 16.0203 8.91483H15.964C13.6314 8.92 11.7377 7.03055 11.7377 4.69797Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  id="Vector_3"
                  d="M13.1465 4.69766C13.1465 6.86656 15.4944 8.22213 17.3727 7.13768C18.2445 6.63436 18.7815 5.7043 18.7815 4.69766C18.7815 2.52876 16.4336 1.1732 14.5552 2.25765C13.6835 2.76097 13.1465 3.69103 13.1465 4.69766Z"
                  fill="#111b31"
                  className="svg__fill"
                />
              </g>
            </svg>
            <div className="relative z-[9999999]">
              {isSuperuser ? "Alerts" : "My Inbox"}
              <div
                className={`text-[0.625rem] !text-[white] bg-[#F00] min-w-4 h-4 rounded-[1rem] px-1 py-1 flex justify-center items-center absolute top-0 right-[-2rem] ${
                  notifications?.data != 0 ? "" : "hidden"
                }`}
              >
                {notifications?.data ? notifications.data : 0}
              </div>
            </div>
            <div className="sidebar__container__options__indicator" />
          </div>
          <div
            onClick={
              isSuperuser && selectedClient
                ? () => {}
                : () => canSelect("Dashboard")
            }
            className={`${
              canAccessDashboard || isSuperuser ? "" : "inaccesable"
            } sidebar__container__options__button Text dark-blue bold ${
              activeComponent === "Dashboard" ? "active" : ""
            } ${isSuperuser && selectedClient ? "inaccesable" : ""} `}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Group 1000002875">
                <path
                  id="Vector"
                  d="M0.0049709 14.4471C0.0049709 13.2597 0.0205751 12.0723 0.00028964 10.8854C-0.0184354 9.77711 0.873604 8.91323 1.84939 8.88774C1.94457 8.88514 2.03976 8.87318 2.13494 8.87318C3.67768 8.87214 5.22041 8.87058 6.76315 8.87526C6.96964 8.87578 7.18186 8.88878 7.38211 8.93455C8.25803 9.13375 8.85463 9.86553 8.903 10.7799C8.90612 10.8402 8.90612 10.901 8.90612 10.9619C8.90612 13.3107 8.9082 15.6589 8.90612 18.0077C8.90508 18.9907 8.25491 19.7797 7.29837 19.9653C7.1803 19.9882 7.05755 19.9929 6.93635 19.9929C5.28075 19.9945 3.62566 19.9971 1.97006 19.9929C0.858 19.9898 0.00965216 19.1436 0.00549104 18.0352C0.00132992 16.8395 0.00445076 15.6433 0.00445076 14.4476L0.0049709 14.4471Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  id="Vector_2"
                  d="M19.9946 5.56937C19.9946 6.74791 19.978 7.92646 19.9998 9.10448C20.0201 10.1847 19.1634 11.0866 18.133 11.1121C18.003 11.1152 17.8735 11.1261 17.7435 11.1266C16.2876 11.1277 14.8317 11.1297 13.3759 11.1245C13.1257 11.1235 12.8692 11.1183 12.6269 11.0653C11.7312 10.8692 11.1205 10.1098 11.0966 9.17677C11.0846 8.70921 11.0935 8.24112 11.0935 7.77303C11.0935 5.86686 11.0935 3.96018 11.0935 2.05402C11.0935 1.49335 11.2594 0.996136 11.6594 0.591499C12.036 0.210786 12.4921 0.00690721 13.031 0.00638711C14.7121 0.00482682 16.3932 0.00118612 18.0743 0.00742731C19.1364 0.0115881 19.9863 0.865591 19.9925 1.93023C19.9993 3.14311 19.9941 4.3565 19.9941 5.56937H19.9946Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  id="Vector_3"
                  d="M4.44099 6.67874C3.62645 6.67874 2.81191 6.68134 1.99737 6.67874C0.850986 6.67406 0.00939958 5.83722 0.0057586 4.69613C0.00263776 3.79531 0.00263776 2.89398 0.0057586 1.99317C0.00939958 0.848952 0.848905 0.00795166 1.99217 0.00639136C3.62957 0.00379087 5.26749 0.020434 6.90489 0.000150173C7.84322 -0.0118121 8.86946 0.691362 8.90015 1.93856C8.92355 2.89086 8.92251 3.84472 8.90171 4.79755C8.87882 5.84763 8.01643 6.67094 6.96263 6.6777C6.12208 6.68342 5.28154 6.67874 4.44151 6.67874H4.44099Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  id="Vector_4"
                  d="M15.5436 19.9929C14.7285 19.9929 13.9135 19.9742 13.0995 19.9981C12.1851 20.0252 11.111 19.3204 11.0959 18.0108C11.0855 17.1095 11.085 16.2082 11.0959 15.3068C11.1099 14.1616 11.9593 13.3247 13.0984 13.3227C14.728 13.3195 16.3576 13.3195 17.9872 13.3227C19.1518 13.3247 19.9903 14.1605 19.9939 15.3193C19.9965 16.2118 19.9788 17.1053 19.9986 17.9973C20.0225 19.0765 19.1289 20.0236 17.9877 19.9966C17.1732 19.9773 16.3581 19.9929 15.5436 19.9929Z"
                  fill="#111b31"
                  className="svg__fill"
                />
              </g>
            </svg>
            My Dashboard
          </div>
          <div
            onClick={isSuperuser ? () => {} : () => canSelect("Exchange")}
            className={`${
              canAccessExchange || isSuperuser ? "" : "inaccesable"
            } sidebar__container__options__button Text dark-blue bold ${
              activeComponent === "Exchange" ? "active" : ""
            } ${isSuperuser ? "inaccesable" : ""} `}
          >
            <svg
              width="20"
              height="19"
              viewBox="0 0 28 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Iconly/Bold/Swap">
                <g id="Swap">
                  <path
                    id="Vector"
                    d="M20.8393 13.1058C22.4481 13.1058 23.9047 13.1977 24.8007 13.3687C24.8147 13.3687 25.6346 13.5382 25.9079 13.6475C26.3023 13.8185 26.6362 14.1273 26.849 14.5137C27.0011 14.8226 27.0757 15.1488 27.0757 15.4893C27.0617 15.844 26.8334 16.5108 26.7263 16.7737C26.0601 18.5062 23.8736 21.8177 22.5397 23.0878C22.3269 23.3032 22.0691 23.536 22.0086 23.5977C21.6732 23.8606 21.2647 24 20.8253 24C20.4293 24 20.0348 23.8765 19.7165 23.6278C19.5511 23.5091 19.3097 23.2727 19.1994 23.1619L19.1404 23.1021C17.7583 21.8034 15.6805 18.568 15.0128 16.9273C14.9985 16.9273 14.6659 16.1089 14.5986 15.6151L14.5873 15.4893V15.4275C14.5873 14.7149 14.9817 14.0497 15.62 13.7092C15.9694 13.5239 16.985 13.3529 17.0005 13.3371C17.9105 13.1977 19.3065 13.1058 20.8393 13.1058ZM6.78157 13.1874C7.41749 13.1874 7.94092 13.6749 8.01245 14.3037L8.02079 14.4511L8.36087 20.5561C8.36087 21.4461 7.6543 22.1667 6.78157 22.1667C5.96339 22.1667 5.28988 21.5334 5.20889 20.7209L5.20073 20.5561L5.54236 14.4511C5.54236 13.7527 6.09675 13.1874 6.78157 13.1874ZM6.7898 0C7.18269 0 7.57712 0.123525 7.89702 0.370574C8.03484 0.470542 8.22543 0.651548 8.349 0.773245L8.47314 0.89793C9.85367 2.19811 11.933 5.43192 12.6007 7.07258C12.6136 7.07258 12.9474 7.8923 13.0149 8.38632L13.0262 8.51212V8.57388C13.0262 9.28494 12.6302 9.95007 11.9936 10.2906C11.6442 10.4774 10.6286 10.6469 10.613 10.6627C9.70304 10.8021 8.30698 10.8939 6.77428 10.8939C5.16547 10.8939 3.70886 10.8021 2.81284 10.631C2.79731 10.631 1.97893 10.4616 1.70562 10.3523C1.31118 10.1829 0.977312 9.87247 0.764565 9.48606C0.612381 9.17725 0.537842 8.85102 0.537842 8.51212C0.551818 8.1558 0.780094 7.49066 0.885691 7.22778C1.55344 5.49368 3.73836 2.18227 5.07385 0.913766C5.2866 0.696806 5.54438 0.464009 5.60494 0.402247C5.93882 0.139361 6.34878 0 6.7898 0ZM20.8323 1.83333C21.6504 1.83333 22.3226 2.46665 22.4034 3.27918L22.4116 3.44394L22.0715 9.54904C22.0715 10.2474 21.5171 10.8128 20.8323 10.8128C20.1964 10.8128 19.6729 10.3253 19.6014 9.69651L19.5931 9.54904L19.2514 3.44394C19.2514 2.55391 19.9595 1.83333 20.8323 1.83333Z"
                    fill="#111b31"
                    className="svg__fill"
                  />
                </g>
              </g>
            </svg>
            Convert
          </div>
          <div
            onClick={
              (canAccessTransactionsNEW && !selectedClient) ||
              canAccessTransactionClient
                ? () => onSelect("Transactions")
                : () => {}
            }
            className={`${
              canAccessTransactionsNEW || canAccessTransactionClient
                ? ""
                : "inaccesable"
            } sidebar__container__options__button Text dark-blue bold ${
              activeComponent === "Transactions" ? "active" : ""
            } ${
              (canAccessTransactionsNEW && !selectedClient) ||
              canAccessTransactionClient
                ? ""
                : "inaccesable"
            }`}
          >
            <svg
              width="20"
              height="19"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Iconly/Bold/Activity">
                <g id="Activity">
                  <path
                    id="Vector"
                    d="M18.5996 3.04042C18.5463 3.37375 18.5196 3.70709 18.5196 4.04042C18.5196 7.04042 20.9463 9.46575 23.9329 9.46575C24.2663 9.46575 24.5863 9.42709 24.9196 9.37375V20.1324C24.9196 24.6538 22.2529 27.3338 17.7196 27.3338H7.86759C3.33293 27.3338 0.66626 24.6538 0.66626 20.1324V10.2671C0.66626 5.73375 3.33293 3.04042 7.86759 3.04042H18.5996ZM18.8676 11.1471C18.5063 11.1071 18.1476 11.2671 17.9329 11.5604L14.7076 15.7338L11.0129 12.8271C10.7863 12.6538 10.5196 12.5858 10.2529 12.6138C9.98759 12.6538 9.74759 12.7991 9.58626 13.0124L5.64093 18.1471L5.55959 18.2671C5.33293 18.6924 5.43959 19.2391 5.83959 19.5338C6.02626 19.6538 6.22626 19.7338 6.45293 19.7338C6.76093 19.7471 7.05293 19.5858 7.23959 19.3338L10.5863 15.0258L14.3863 17.8804L14.5063 17.9591C14.9329 18.1858 15.4663 18.0804 15.7729 17.6791L19.6263 12.7071L19.5729 12.7338C19.7863 12.4404 19.8263 12.0671 19.6796 11.7338C19.5343 11.4004 19.2129 11.1738 18.8676 11.1471ZM24.1197 0.666687C25.8931 0.666687 27.3331 2.10669 27.3331 3.88002C27.3331 5.65335 25.8931 7.09335 24.1197 7.09335C22.3464 7.09335 20.9064 5.65335 20.9064 3.88002C20.9064 2.10669 22.3464 0.666687 24.1197 0.666687Z"
                    fill="#111b31"
                    className="svg__fill"
                  />
                </g>
              </g>
            </svg>
            Transactions
          </div>
          {!user?.is_staff && (
            <div
              onClick={
                (canAccessTransactionsNEW && !selectedClient) ||
                canAccessTransactionClient
                  ? () => onSelect("POS Clients")
                  : () => {}
              }
              className={`${
                canAccessTransactionsNEW || canAccessTransactionClient
                  ? ""
                  : "inaccesable"
              } sidebar__container__options__button Text dark-blue bold ${
                activeComponent === "POS Clients" ? "active" : ""
              } ${
                (canAccessTransactionsNEW && !selectedClient) ||
                canAccessTransactionClient
                  ? ""
                  : "inaccesable"
              }`}
            >
              {isBitramp() ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="19"
                  height="19"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M18 14.5C17.0946 14.499 16.2012 14.7078 15.39 15.11C15.3252 15.139 15.2676 15.1818 15.2209 15.2353C15.1743 15.2888 15.1399 15.3519 15.12 15.42C15.0895 15.5562 15.1109 15.6988 15.18 15.82C16.0464 17.227 16.5035 18.8476 16.5 20.5C16.5026 20.6318 16.5561 20.7575 16.6493 20.8507C16.7425 20.9439 16.8682 20.9974 17 21H23.5C23.6326 21 23.7598 20.9473 23.8536 20.8536C23.9473 20.7598 24 20.6326 24 20.5C24 18.9087 23.3679 17.3826 22.2427 16.2574C21.1174 15.1321 19.5913 14.5 18 14.5Z"
                    fill="#fff"
                  />
                  <path
                    d="M14.1899 10.25C14.1899 10.7425 14.2869 11.2301 14.4754 11.6851C14.6638 12.14 14.9401 12.5534 15.2883 12.9017C15.6365 13.2499 16.0499 13.5261 16.5049 13.7145C16.9598 13.903 17.4475 14 17.9399 14C18.4324 14 18.92 13.903 19.375 13.7145C19.83 13.5261 20.2434 13.2499 20.5916 12.9017C20.9398 12.5534 21.216 12.14 21.4045 11.6851C21.5929 11.2301 21.6899 10.7425 21.6899 10.25C21.6899 9.75754 21.5929 9.26991 21.4045 8.81494C21.216 8.35997 20.9398 7.94657 20.5916 7.59835C20.2434 7.25013 19.83 6.97391 19.375 6.78545C18.92 6.597 18.4324 6.5 17.9399 6.5C17.4475 6.5 16.9598 6.597 16.5049 6.78545C16.0499 6.97391 15.6365 7.25013 15.2883 7.59835C14.9401 7.94657 14.6638 8.35997 14.4754 8.81494C14.2869 9.26991 14.1899 9.75754 14.1899 10.25Z"
                    fill="#fff"
                  />
                  <path
                    d="M2.75 7.75C2.75 9.00978 3.25045 10.218 4.14124 11.1088C5.03204 11.9996 6.24022 12.5 7.5 12.5C8.75978 12.5 9.96796 11.9996 10.8588 11.1088C11.7496 10.218 12.25 9.00978 12.25 7.75C12.25 6.49022 11.7496 5.28204 10.8588 4.39124C9.96796 3.50045 8.75978 3 7.5 3C6.24022 3 5.03204 3.50045 4.14124 4.39124C3.25045 5.28204 2.75 6.49022 2.75 7.75Z"
                    fill="#fff"
                  />
                  <path
                    d="M15 20.5C15 18.5109 14.2098 16.6032 12.8033 15.1967C11.3968 13.7902 9.48912 13 7.5 13C5.51088 13 3.60322 13.7902 2.1967 15.1967C0.790176 16.6032 0 18.5109 0 20.5C0 20.6326 0.0526784 20.7598 0.146447 20.8536C0.240215 20.9473 0.367392 21 0.5 21H14.5C14.6326 21 14.7598 20.9473 14.8536 20.8536C14.9473 20.7598 15 20.6326 15 20.5Z"
                    fill="#fff"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="19"
                  height="19"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M18 14.5C17.0946 14.499 16.2012 14.7078 15.39 15.11C15.3252 15.139 15.2676 15.1818 15.2209 15.2353C15.1743 15.2888 15.1399 15.3519 15.12 15.42C15.0895 15.5562 15.1109 15.6988 15.18 15.82C16.0464 17.227 16.5035 18.8476 16.5 20.5C16.5026 20.6318 16.5561 20.7575 16.6493 20.8507C16.7425 20.9439 16.8682 20.9974 17 21H23.5C23.6326 21 23.7598 20.9473 23.8536 20.8536C23.9473 20.7598 24 20.6326 24 20.5C24 18.9087 23.3679 17.3826 22.2427 16.2574C21.1174 15.1321 19.5913 14.5 18 14.5Z"
                    fill="#202A43"
                  />
                  <path
                    d="M14.1899 10.25C14.1899 10.7425 14.2869 11.2301 14.4754 11.6851C14.6638 12.14 14.9401 12.5534 15.2883 12.9017C15.6365 13.2499 16.0499 13.5261 16.5049 13.7145C16.9598 13.903 17.4475 14 17.9399 14C18.4324 14 18.92 13.903 19.375 13.7145C19.83 13.5261 20.2434 13.2499 20.5916 12.9017C20.9398 12.5534 21.216 12.14 21.4045 11.6851C21.5929 11.2301 21.6899 10.7425 21.6899 10.25C21.6899 9.75754 21.5929 9.26991 21.4045 8.81494C21.216 8.35997 20.9398 7.94657 20.5916 7.59835C20.2434 7.25013 19.83 6.97391 19.375 6.78545C18.92 6.597 18.4324 6.5 17.9399 6.5C17.4475 6.5 16.9598 6.597 16.5049 6.78545C16.0499 6.97391 15.6365 7.25013 15.2883 7.59835C14.9401 7.94657 14.6638 8.35997 14.4754 8.81494C14.2869 9.26991 14.1899 9.75754 14.1899 10.25Z"
                    fill="#202A43"
                  />
                  <path
                    d="M2.75 7.75C2.75 9.00978 3.25045 10.218 4.14124 11.1088C5.03204 11.9996 6.24022 12.5 7.5 12.5C8.75978 12.5 9.96796 11.9996 10.8588 11.1088C11.7496 10.218 12.25 9.00978 12.25 7.75C12.25 6.49022 11.7496 5.28204 10.8588 4.39124C9.96796 3.50045 8.75978 3 7.5 3C6.24022 3 5.03204 3.50045 4.14124 4.39124C3.25045 5.28204 2.75 6.49022 2.75 7.75Z"
                    fill="#202A43"
                  />
                  <path
                    d="M15 20.5C15 18.5109 14.2098 16.6032 12.8033 15.1967C11.3968 13.7902 9.48912 13 7.5 13C5.51088 13 3.60322 13.7902 2.1967 15.1967C0.790176 16.6032 0 18.5109 0 20.5C0 20.6326 0.0526784 20.7598 0.146447 20.8536C0.240215 20.9473 0.367392 21 0.5 21H14.5C14.6326 21 14.7598 20.9473 14.8536 20.8536C14.9473 20.7598 15 20.6326 15 20.5Z"
                    fill="#202A43"
                  />
                </svg>
              )}
              POS Clients
            </div>
          )}
          <div
            onClick={
              isSuperuser && !selectedClient
                ? () => {}
                : () => canSelect("Clude")
            }
            className={`sidebar__container__options__button Text dark-blue bold ${
              activeComponent === "Clude" ? "active" : ""
            } ${isSuperuser && !selectedClient ? "inaccesable" : ""}`}
          >
            <svg
              width="22"
              height="14"
              viewBox="0 0 24 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Microsoft-Onedrive-Logo-2--Streamline-Ultimate.svg">
                <path
                  id="Vector"
                  d="M21.5287 9.12214C21.5162 6.37594 18.5355 4.67304 16.1634 6.05704C16.0836 6.10364 16.0056 6.15324 15.9296 6.20594C14.0189 3.73894 10.1541 4.26554 8.973 7.15374C8.866 7.41524 8.7867 7.68734 8.7363 7.96534C5.9826 8.03644 4.3385 11.0618 5.7769 13.4111C6.4445 14.5014 7.643 15.1527 8.921 15.1197H20.3622C22.7488 15.1671 24.2917 12.6132 23.1395 10.5226C22.7852 9.87994 22.2144 9.38364 21.5287 9.12214Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  id="Vector_2"
                  d="M7.2881 3.8629C7.7998 3.8565 8.2992 4.0207 8.7073 4.3295C8.8992 4.4737 9.1487 4.5171 9.378 4.4462C9.6078 4.3759 9.7891 4.1982 9.864 3.9699C10.6919 1.3592 14.0355 0.6238 15.8824 2.6461C16.1974 2.991 16.4414 3.3946 16.6004 3.8338C16.749 4.375 17.4277 4.5524 17.8221 4.1531C18.0321 3.9405 18.0911 3.6213 17.971 3.3477C16.6857 -0.2932 11.9408 -1.1774 9.4303 1.7563C9.1836 2.0446 8.9704 2.36 8.7948 2.6964C8.3179 2.4967 7.805 2.3975 7.2881 2.4048C5.182 2.4047 3.4732 4.1092 3.4679 6.2153C0.706702 6.3126 -0.913798 9.3625 0.551102 11.7051C1.2309 12.7923 2.4391 13.4343 3.7206 13.3891C4.2818 13.387 4.6303 12.7782 4.3479 12.2932C4.2178 12.0698 3.9791 11.932 3.7206 11.931C2.0856 11.8203 1.1836 9.9811 2.097 8.6205C2.464 8.0737 3.0635 7.7276 3.7206 7.6831C3.8628 7.6683 4.0061 7.6683 4.1483 7.6831C4.3952 7.7377 4.6527 7.6604 4.8288 7.479C4.9933 7.2975 5.0587 7.047 5.0037 6.8083C4.9534 6.6146 4.9273 6.4154 4.926 6.2153C4.9313 4.9145 5.9873 3.8629 7.2881 3.8629Z"
                  fill="#111b31"
                  className="svg__fill"
                />
              </g>
            </svg>
            Cloud
          </div>
          <div
            onClick={() => canSelect("Reports")}
            className={`${
              canAccessReports || isSuperuser ? "" : "inaccesable"
            } sidebar__container__options__button Text dark-blue bold ${
              activeComponent === "Reports" ? "active" : ""
            } `}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="18"
              viewBox="0 0 18 23"
              fill="none"
            >
              <g clipPath="url(#clip0_2817_185)">
                <path
                  d="M11.0322 0L11.4811 0.142251C12.0335 0.380028 12.4716 0.787831 12.9187 1.1676C14.2024 2.48654 15.6141 3.7006 16.8609 5.04939C17.3576 5.58672 17.8822 6.16819 17.9967 6.91682C17.9764 11.2682 18.0318 15.6294 17.9687 19.9746C17.7224 21.5819 16.3803 22.8372 14.7392 23.0003H3.2632C1.59962 22.8282 0.264954 21.5495 0.0336303 19.9227C-0.0112101 14.3248 -0.0112101 8.70067 0.0336303 3.10278C0.244908 1.49753 1.59144 0.165354 3.23946 0.0264774L3.27744 0H11.0322ZM3.45469 1.77061C2.52755 1.86432 1.83648 2.6075 1.80113 3.51786C1.81775 8.92417 1.76526 14.3406 1.82751 19.7407C1.96625 20.5405 2.62303 21.1554 3.44783 21.2351H14.6076C15.4659 21.118 16.1118 20.46 16.2034 19.6127V7.87727C16.1606 7.36979 15.776 7.19613 15.3181 7.11307C14.0447 7.03415 12.7475 7.14214 11.4727 7.0557C11.0844 6.94148 10.8476 6.63388 10.7956 6.24243C10.7777 5.02421 10.8294 3.79535 10.7682 2.58336C10.7035 2.20956 10.5471 1.83343 10.1235 1.76412L3.45469 1.77061Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  d="M4.2196 15.9431L13.6833 15.9385C14.669 16.1503 14.641 17.5157 13.658 17.6779C10.554 17.7361 7.43149 17.6873 4.32062 17.7026C3.31435 17.5388 3.24366 16.2191 4.2196 15.9431Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  d="M4.24538 8.85695L13.6297 8.85254C14.6687 9.0288 14.6705 10.4388 13.6297 10.6164H4.26727C3.28948 10.3861 3.27419 9.10979 4.24538 8.85695Z"
                  fill="#111b31"
                  className="svg__fill"
                />
                <path
                  d="M4.32569 12.388L11.7576 12.3828C12.8174 12.4882 12.925 13.8962 11.8895 14.1469L4.55596 14.1462C4.50057 14.149 4.46206 14.1799 4.39981 14.1734C3.27089 14.0584 3.22341 12.5669 4.32543 12.3877L4.32569 12.388Z"
                  fill="#111b31"
                  className="svg__fill"
                />
              </g>
              <defs>
                <clipPath id="clip0_2817_185">
                  <rect width="18" height="23" fill="#111b31" />
                </clipPath>
              </defs>
            </svg>
            Reports
          </div>
          {user?.is_staff ? (
            <div
              onClick={() => {
                canAccessClientsNEW ? canSelect("Logs") : null;
              }}
              className={`${
                canAccessClientsNEW ? "" : "inaccesable"
              } sidebar__container__options__button Text dark-blue bold ${
                activeComponent === "Logs" ? "active" : ""
              } `}
            >
              {isBitramp() ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="20"
                  viewBox="0 0 20 23"
                  fill="none"
                >
                  <g clip-path="url(#clip0_4795_7918)">
                    <path
                      d="M1.06419 10.6902L18.8908 10.6816C20.3847 10.9122 20.365 12.957 18.8574 13.1471H1.1553C-0.316869 12.9702 -0.385621 10.9747 1.06419 10.6902Z"
                      fill="white"
                    />
                    <path
                      d="M1.06419 15.6169L18.8908 15.6084C20.3847 15.8389 20.365 17.8838 18.8574 18.0738H1.1553C-0.316869 17.8969 -0.385621 15.9014 1.06419 15.6169Z"
                      fill="white"
                    />
                    <path
                      d="M1.06415 20.543L12.2545 20.5371C13.7243 20.8005 13.6813 22.8125 12.1907 22.9996L1.1249 22.9966C-0.324916 22.7898 -0.369304 20.8219 1.06415 20.5427V20.543Z"
                      fill="white"
                    />
                    <path
                      d="M8.74857 2.16647C8.14948 1.55479 8.20355 0.621495 8.9625 0.177208C9.47948 -0.12567 10.0038 -0.0122141 10.457 0.335718C10.8726 0.654381 11.2867 1.07466 11.6899 1.41996C12.3367 1.97442 13.0496 2.52986 13.6744 3.11029C14.3489 3.7371 14.3486 4.48887 13.6744 5.11567C12.8807 5.8533 11.9806 6.56363 11.1566 7.26574C10.5138 7.81362 10.0345 8.50685 9.07331 8.13623C8.23726 7.81395 8.06505 6.70142 8.70585 6.09895C9.38069 5.46459 10.158 4.85883 10.8565 4.24387C10.8956 4.20966 11.0204 4.16362 11.0061 4.09818C10.8031 3.9377 10.6186 3.75584 10.4237 3.58681C9.89266 3.12641 9.3333 2.691 8.82299 2.20659C8.8023 2.18719 8.76525 2.18324 8.74857 2.16647Z"
                      fill="white"
                    />
                    <path
                      d="M5.53106 0.410093C5.80607 0.697186 5.89719 1.18455 5.79806 1.55945C5.6669 2.0557 5.18463 2.35003 4.82251 2.6677C4.27549 3.14751 3.73215 3.62567 3.17345 4.09856C3.1591 4.16401 3.28392 4.20972 3.32297 4.24425C3.73415 4.60632 4.14233 4.96938 4.55584 5.32849C5.1626 5.85565 6.08008 6.36538 5.83111 7.30723C5.68793 7.84853 5.21033 8.19778 4.65397 8.24941C4.02718 8.30729 3.45614 7.6351 3.02259 7.26579C2.19856 6.56368 1.29844 5.85302 0.504781 5.11572C-0.169395 4.48925 -0.169729 3.73748 0.504781 3.11035C1.29844 2.37272 2.19856 1.66271 3.02259 0.960272C3.60332 0.465341 4.13866 -0.239731 5.01575 0.0805769C5.16227 0.134181 5.4256 0.299925 5.5314 0.410093H5.53106Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4795_7918">
                      <rect width="20" height="23" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="20"
                  viewBox="0 0 20 23"
                  fill="none"
                >
                  <g clip-path="url(#clip0_4795_7918)">
                    <path
                      d="M1.06419 10.6902L18.8908 10.6816C20.3847 10.9122 20.365 12.957 18.8574 13.1471H1.1553C-0.316869 12.9702 -0.385621 10.9747 1.06419 10.6902Z"
                      fill="black"
                    />
                    <path
                      d="M1.06419 15.6169L18.8908 15.6084C20.3847 15.8389 20.365 17.8838 18.8574 18.0738H1.1553C-0.316869 17.8969 -0.385621 15.9014 1.06419 15.6169Z"
                      fill="black"
                    />
                    <path
                      d="M1.06415 20.543L12.2545 20.5371C13.7243 20.8005 13.6813 22.8125 12.1907 22.9996L1.1249 22.9966C-0.324916 22.7898 -0.369304 20.8219 1.06415 20.5427V20.543Z"
                      fill="black"
                    />
                    <path
                      d="M8.74857 2.16647C8.14948 1.55479 8.20355 0.621495 8.9625 0.177208C9.47948 -0.12567 10.0038 -0.0122141 10.457 0.335718C10.8726 0.654381 11.2867 1.07466 11.6899 1.41996C12.3367 1.97442 13.0496 2.52986 13.6744 3.11029C14.3489 3.7371 14.3486 4.48887 13.6744 5.11567C12.8807 5.8533 11.9806 6.56363 11.1566 7.26574C10.5138 7.81362 10.0345 8.50685 9.07331 8.13623C8.23726 7.81395 8.06505 6.70142 8.70585 6.09895C9.38069 5.46459 10.158 4.85883 10.8565 4.24387C10.8956 4.20966 11.0204 4.16362 11.0061 4.09818C10.8031 3.9377 10.6186 3.75584 10.4237 3.58681C9.89266 3.12641 9.3333 2.691 8.82299 2.20659C8.8023 2.18719 8.76525 2.18324 8.74857 2.16647Z"
                      fill="black"
                    />
                    <path
                      d="M5.53106 0.410093C5.80607 0.697186 5.89719 1.18455 5.79806 1.55945C5.6669 2.0557 5.18463 2.35003 4.82251 2.6677C4.27549 3.14751 3.73215 3.62567 3.17345 4.09856C3.1591 4.16401 3.28392 4.20972 3.32297 4.24425C3.73415 4.60632 4.14233 4.96938 4.55584 5.32849C5.1626 5.85565 6.08008 6.36538 5.83111 7.30723C5.68793 7.84853 5.21033 8.19778 4.65397 8.24941C4.02718 8.30729 3.45614 7.6351 3.02259 7.26579C2.19856 6.56368 1.29844 5.85302 0.504781 5.11572C-0.169395 4.48925 -0.169729 3.73748 0.504781 3.11035C1.29844 2.37272 2.19856 1.66271 3.02259 0.960272C3.60332 0.465341 4.13866 -0.239731 5.01575 0.0805769C5.16227 0.134181 5.4256 0.299925 5.5314 0.410093H5.53106Z"
                      fill="black"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4795_7918">
                      <rect width="20" height="23" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              )}
              Logs
            </div>
          ) : null}
          {user?.is_staff ? (
            <div
              onClick={() => {
                canAccessClientsNEW ? canSelect("Clients") : null;
              }}
              className={`${
                canAccessClientsNEW ? "" : "inaccesable"
              } sidebar__container__options__button Text dark-blue bold ${
                activeComponent === "Clients" ? "active" : ""
              } `}
            >
              <svg
                width="20"
                height="14"
                viewBox="0 0 24 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g id="Multiple-Neutral-2--Streamline-Ultimate.svg">
                  <path
                    id="Vector"
                    d="M18 11.5C17.0946 11.499 16.2012 11.7078 15.39 12.11C15.3252 12.139 15.2676 12.1818 15.2209 12.2353C15.1743 12.2888 15.1399 12.3519 15.12 12.42C15.0895 12.5562 15.1109 12.6988 15.18 12.82C16.0464 14.227 16.5035 15.8476 16.5 17.5C16.5026 17.6318 16.5561 17.7575 16.6493 17.8507C16.7425 17.9439 16.8682 17.9974 17 18H23.5C23.6326 18 23.7598 17.9473 23.8536 17.8536C23.9473 17.7598 24 17.6326 24 17.5C24 15.9087 23.3679 14.3826 22.2427 13.2574C21.1174 12.1321 19.5913 11.5 18 11.5Z"
                    fill="#111b31"
                    className="svg__fill"
                  />
                  <path
                    id="Vector_2"
                    d="M14.1899 7.25C14.1899 7.74246 14.2869 8.23009 14.4754 8.68506C14.6638 9.14003 14.9401 9.55343 15.2883 9.90165C15.6365 10.2499 16.0499 10.5261 16.5049 10.7145C16.9598 10.903 17.4475 11 17.9399 11C18.4324 11 18.92 10.903 19.375 10.7145C19.83 10.5261 20.2434 10.2499 20.5916 9.90165C20.9398 9.55343 21.216 9.14003 21.4045 8.68506C21.5929 8.23009 21.6899 7.74246 21.6899 7.25C21.6899 6.75754 21.5929 6.26991 21.4045 5.81494C21.216 5.35997 20.9398 4.94657 20.5916 4.59835C20.2434 4.25013 19.83 3.97391 19.375 3.78545C18.92 3.597 18.4324 3.5 17.9399 3.5C17.4475 3.5 16.9598 3.597 16.5049 3.78545C16.0499 3.97391 15.6365 4.25013 15.2883 4.59835C14.9401 4.94657 14.6638 5.35997 14.4754 5.81494C14.2869 6.26991 14.1899 6.75754 14.1899 7.25Z"
                    fill="#111b31"
                    className="svg__fill"
                  />
                  <path
                    id="Vector_3"
                    d="M2.75 4.75C2.75 6.00978 3.25045 7.21796 4.14124 8.10876C5.03204 8.99955 6.24022 9.5 7.5 9.5C8.75978 9.5 9.96796 8.99955 10.8588 8.10876C11.7496 7.21796 12.25 6.00978 12.25 4.75C12.25 3.49022 11.7496 2.28204 10.8588 1.39124C9.96796 0.500445 8.75978 0 7.5 0C6.24022 0 5.03204 0.500445 4.14124 1.39124C3.25045 2.28204 2.75 3.49022 2.75 4.75Z"
                    fill="#111b31"
                    className="svg__fill"
                  />
                  <path
                    id="Vector_4"
                    d="M15 17.5C15 15.5109 14.2098 13.6032 12.8033 12.1967C11.3968 10.7902 9.48912 10 7.5 10C5.51088 10 3.60322 10.7902 2.1967 12.1967C0.790176 13.6032 0 15.5109 0 17.5C0 17.6326 0.0526784 17.7598 0.146447 17.8536C0.240215 17.9473 0.367392 18 0.5 18H14.5C14.6326 18 14.7598 17.9473 14.8536 17.8536C14.9473 17.7598 15 17.6326 15 17.5Z"
                    fill="#111b31"
                    className="svg__fill"
                  />
                </g>
              </svg>
              Clients
            </div>
          ) : null}
          {canAccessStatus ? (
            <div
              onClick={() => onSelect("Status")}
              className={`${
                canAccessStatus || isSuperuser ? "" : "inaccesable"
              } sidebar__container__options__button Text dark-blue bold ${
                activeComponent === "Status" ? "active" : ""
              } `}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  id="Vector"
                  d="M10.9529 22C4.94907 22.0374 0.0199151 17.0799 5.95103e-05 10.9851C-0.0197961 5.07447 4.93169 0.0124783 10.7494 3.48647e-05C17.0709 -0.0148973 21.9802 4.76836 22.0025 10.9627C22.0249 17.1147 17.1727 21.9627 10.9554 22H10.9529ZM12.6158 10.6914V6.66972C12.6158 6.10727 12.1616 5.65433 11.6032 5.65433H10.4838C9.92289 5.65433 9.47118 6.10976 9.47118 6.66972V10.6914C9.47118 11.2539 9.92538 11.7068 10.4838 11.7068H11.6032C12.1641 11.7068 12.6158 11.2514 12.6158 10.6914ZM12.5984 14.9098C12.5984 14.096 11.9407 13.434 11.1266 13.434H10.9455C10.1339 13.434 9.47366 14.0935 9.47366 14.9098C9.47366 15.7236 10.1314 16.3856 10.9455 16.3856H11.1266C11.9382 16.3856 12.5984 15.7261 12.5984 14.9098Z"
                  fill="#111b31"
                  className="svg__fill"
                />
              </svg>
              Status
            </div>
          ) : null}
          <div
            onClick={() => {
              canSelect("Settings");
            }}
            className={`sidebar__container__options__button Text dark-blue bold ${
              activeComponent === "Settings" ? "active" : ""
            } `}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 26 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Iconly/Bold/Setting">
                <g id="Setting">
                  <path
                    id="Vector"
                    d="M13.956 0.666748C14.9643 0.666748 15.8773 1.22675 16.3814 2.05341C16.6267 2.45341 16.7902 2.94675 16.7493 3.46675C16.7221 3.86675 16.8447 4.26675 17.0627 4.64008C17.7577 5.77342 19.2974 6.20008 20.4965 5.56008C21.8455 4.78675 23.5487 5.25342 24.3254 6.57342L25.2384 8.14675C26.0287 9.46675 25.5927 11.1601 24.2301 11.9201C23.0718 12.6001 22.6631 14.1067 23.358 15.2534C23.576 15.6134 23.8213 15.9201 24.2028 16.1067C24.6797 16.3601 25.0476 16.7601 25.3065 17.1601C25.8107 17.9867 25.7698 19.0001 25.2793 19.8934L24.3254 21.4934C23.8213 22.3468 22.8811 22.8801 21.9136 22.8801C21.4367 22.8801 20.9053 22.7468 20.4692 22.4801C20.115 22.2534 19.7062 22.1734 19.2702 22.1734C17.9212 22.1734 16.7902 23.2801 16.7493 24.6001C16.7493 26.1334 15.4957 27.3334 13.9287 27.3334H12.0756C10.4949 27.3334 9.24135 26.1334 9.24135 24.6001C9.21409 23.2801 8.08313 22.1734 6.73414 22.1734C6.28448 22.1734 5.8757 22.2534 5.53505 22.4801C5.09901 22.7468 4.55397 22.8801 4.09068 22.8801C3.1096 22.8801 2.1694 22.3468 1.66523 21.4934L0.725034 19.8934C0.220869 19.0268 0.193616 17.9867 0.697782 17.1601C0.9158 16.7601 1.32458 16.3601 1.78787 16.1067C2.1694 15.9201 2.41467 15.6134 2.64631 15.2534C3.32762 14.1067 2.91884 12.6001 1.76062 11.9201C0.411634 11.1601 -0.0244012 9.46675 0.752286 8.14675L1.66523 6.57342C2.45555 5.25342 4.14518 4.78675 5.50779 5.56008C6.69326 6.20008 8.23301 5.77342 8.92795 4.64008C9.14596 4.26675 9.2686 3.86675 9.24135 3.46675C9.21409 2.94675 9.36398 2.45341 9.62288 2.05341C10.127 1.22675 11.04 0.693415 12.0347 0.666748H13.956ZM13.0158 10.2401C10.8765 10.2401 9.14596 11.9201 9.14596 14.0134C9.14596 16.1068 10.8765 17.7734 13.0158 17.7734C15.1551 17.7734 16.8447 16.1068 16.8447 14.0134C16.8447 11.9201 15.1551 10.2401 13.0158 10.2401Z"
                    fill="#111b31"
                    className="svg__fill"
                  />
                </g>
              </g>
            </svg>
            Settings
          </div>
        </div>
        <div className=" w-[90%] h-full flex flex-col justify-end pb-4 gap-1.5">
          <div className="w-full">
            <div className="Primary_button w-fit">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
              >
                <g clip-path="url(#clip0_4629_7716)">
                  <path
                    d="M10.9377 9.0733C11.2214 9.14111 11.2659 8.96414 11.4324 8.82521C12.1415 8.23311 12.9908 6.90999 13.9884 6.90999C14.5788 6.90999 15.2845 7.40451 15.3901 7.99826C15.5121 8.6929 15.2318 9.07495 14.8179 9.57443C13.9967 10.5618 11.5017 13.1469 10.5288 13.8729C9.85432 14.3757 9.2986 14.4766 8.62415 13.9606C7.70894 13.261 4.71268 10.2459 4.01184 9.32469C3.48911 8.63832 3.40501 7.87918 4.091 7.2507C5.42835 6.02516 6.73767 8.23808 7.64134 8.99225C7.80789 9.13118 7.85241 9.30815 8.13604 9.24034V0.886492C8.13604 0.850106 8.53016 0.332435 8.57633 0.299357C9.1238 -0.100887 9.81639 -0.0992327 10.3688 0.301011C10.4991 0.395283 10.9394 0.959263 10.9394 1.05188V9.0733H10.9377Z"
                    fill="white"
                  />
                  <path
                    d="M0.323271 16.207L18.8632 16.26L19.0001 16.4535L18.9456 18.8417L18.6109 19.0021L0.211138 18.9244L0.0742695 18.7309C0.15672 17.9519 -0.171434 17.0158 0.133634 16.3476L0.32492 16.207H0.323271Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_4629_7716">
                    <rect width="19" height="19" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              POS App
            </div>
          </div>
          <div
            onClick={handleLogout}
            className="h-fit w-full flex items-center justify-start gap-2 sidebar__container__options__button_red cursor-pointer"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 26 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="Iconly/Bold/Logout">
                <g id="Logout">
                  <path
                    id="Vector"
                    d="M11.9491 0.861328C14.8579 0.861328 17.2302 3.19298 17.2302 6.06361V11.676H10.0775C9.56491 11.676 9.15958 12.0743 9.15958 12.5782C9.15958 13.0703 9.56491 13.4804 10.0775 13.4804H17.2302V19.081C17.2302 21.9516 14.8579 24.295 11.9253 24.295H6.11968C3.19898 24.295 0.82666 21.9634 0.82666 19.0927V6.07532C0.82666 3.19298 3.2109 0.861328 6.1316 0.861328H11.9491ZM20.2066 8.53609C20.5581 8.17287 21.1322 8.17287 21.4837 8.52438L24.905 11.934C25.0808 12.1097 25.1745 12.3324 25.1745 12.5784C25.1745 12.8127 25.0808 13.0471 24.905 13.2111L21.4837 16.6207C21.3079 16.7965 21.0736 16.8902 20.851 16.8902C20.6166 16.8902 20.3823 16.7965 20.2066 16.6207C19.855 16.2692 19.855 15.6951 20.2066 15.3436L22.0812 13.4806H17.2305V11.6762H22.0812L20.2066 9.81323C19.855 9.46173 19.855 8.8876 20.2066 8.53609Z"
                    fill="#FF4842"
                  />
                </g>
              </g>
            </svg>
            <div>Logout</div>
          </div>
          <div
            className={`${
              isBitramp() ? "text-white" : "dark-gray"
            } w-full Text pl-5 `}
          >
            {/* {user?.business_name} */}
            {!user?.is_staff ? user?.business_name : ""}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
